import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Package, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { StatusBadge } from './Dashboard';

export default function ItemPasses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('all');
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    const params = status !== 'all' ? { status } : {};
    const { data } = await api.get('/item-passes', { params });
    setItems(data);
  };

  useEffect(() => { load(); }, [status]);

  const markReturned = async (it) => {
    try {
      await api.post(`/item-passes/${it.id}/return`);
      toast.success('Marked returned');
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data));
    }
  };

  const canMark = ['admin', 'supervisor', 'guard'].includes(user.role);

  return (
    <div className="space-y-6" data-testid="item-passes-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Module 03</div>
          <h1 className="heading text-3xl font-black text-ink">Item / Material Passes</h1>
          <p className="text-sm text-zinc-500 mt-1">Track returnable and non-returnable items leaving the factory.</p>
        </div>
        <button className="btn-primary" onClick={() => setOpenCreate(true)} data-testid="new-item-pass-btn"><Plus size={16} /> New Item Pass</button>
      </header>

      <div className="flex gap-1 flex-wrap">
        {['all', 'pending', 'returned', 'completed'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`item-filter-${s}`}
            className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${status === s ? 'bg-ink text-white border-ink' : 'bg-white text-zinc-700 border-zinc-300'}`}
          >{s}</button>
        ))}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
              <th className="px-4 py-3 overline">Pass ID</th>
              <th className="px-4 py-3 overline">Item</th>
              <th className="px-4 py-3 overline">Qty</th>
              <th className="px-4 py-3 overline">Type</th>
              <th className="px-4 py-3 overline">Vendor</th>
              <th className="px-4 py-3 overline">Return Due</th>
              <th className="px-4 py-3 overline">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="items-table-body">
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-zinc-400">No item passes.</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${i.is_overdue ? 'bg-red-50/50' : ''}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold">{i.pass_id}</td>
                <td className="px-4 py-3 font-semibold">{i.item_name}</td>
                <td className="px-4 py-3">{i.quantity}</td>
                <td className="px-4 py-3">
                  <span className={i.item_type === 'returnable' ? 'badge-warn' : 'badge-muted'}>
                    {i.item_type === 'returnable' ? 'Returnable' : 'Non-return'}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-700">{i.vendor_name}</td>
                <td className="px-4 py-3 text-xs">
                  {i.expected_return_date ? (
                    <span className={i.is_overdue ? 'text-red-600 font-bold' : 'text-zinc-600'}>
                      {i.is_overdue && <AlertTriangle size={12} className="inline mr-1" />}
                      {i.expected_return_date}
                    </span>
                  ) : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                <td className="px-4 py-3">
                  {canMark && i.status === 'pending' && (
                    <button onClick={() => markReturned(i)} className="btn-success text-xs" data-testid={`mark-returned-${i.id}`}>
                      <CheckCircle2 size={12} /> Returned
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openCreate && <CreateItemModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
    </div>
  );
}

function CreateItemModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    item_name: '', quantity: 1, item_type: 'returnable',
    vendor_name: '', vendor_address: '', expected_return_date: '', description: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await api.post('/item-passes', form);
      toast.success('Item pass created');
      onCreated();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New Item / Material Pass" subtitle="Outbound material"
      size="md"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button form="it-form" type="submit" className="btn-primary" disabled={loading} data-testid="submit-item"><Package size={14} /> Create</button>
      </>}
    >
      <form id="it-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-[1fr_100px] gap-4">
          <div><label className="label">Item Name *</label><input required className="input" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} data-testid="it-name" /></div>
          <div><label className="label">Qty</label><input type="number" min={1} className="input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })} data-testid="it-qty" /></div>
        </div>
        <div>
          <label className="label">Type *</label>
          <div className="grid grid-cols-2 gap-2">
            {['returnable', 'non_returnable'].map((t) => (
              <button key={t} type="button" onClick={() => setForm({ ...form, item_type: t })}
                data-testid={`it-type-${t}`}
                className={`py-2 text-sm font-bold uppercase border rounded-sm ${form.item_type === t ? 'bg-primary text-white border-primary' : 'bg-white border-zinc-300'}`}
              >{t.replace('_', ' ')}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label">Vendor Name *</label><input required className="input" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="it-vendor" /></div>
          <div><label className="label">Expected Return</label><input type="date" className="input" value={form.expected_return_date} onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} disabled={form.item_type === 'non_returnable'} /></div>
        </div>
        <div><label className="label">Vendor Address</label>
          <input className="input" value={form.vendor_address} onChange={(e) => setForm({ ...form, vendor_address: e.target.value })} />
        </div>
        <div><label className="label">Description</label>
          <textarea rows={2} className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}
